import React, { useEffect, useState } from 'react';
import IconTint from 'react-icon-tint';
import statusIcon from '../../assets/icons/ic_statuspatient.svg'
import maskIcon from '../../assets/icons/ic_mask.svg'
import checkIcon from '../../assets/icons/ic_check.svg'
import { collection, getDocs, query, where, doc, getDoc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAtom } from 'jotai';
import { currentRobot } from '../store/DataStore';
import { refreshDolphinAPIKey, putGpsStatus } from "../../lib/apiClient";


const CheckinPopup = ({ isOpen, onClose }) => {
  const [show, setShow] = useState(isOpen);
  const [patientsList, setPatientsList] = useState([]);
  const [confirming, setConfirming] = useState(null); // { id, firstName } | null
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [savedRobot] = useAtom(currentRobot);


    async function getRobotCreds(robotSN) {
        const snap = await getDoc(doc(db, `Companies/Zeeno_Dev/Dolphin/${robotSN}`));
        if (!snap.exists()) throw new Error('Robot doc missing');
        const d = snap.data();
        return { practiceId: d.practiceid, dolphinApiKey: d.dolphin_api_key };
    }

    async function getGpsStatusId(practiceId) {
        const snap = await getDoc(doc(db, "Companies", "Zeeno_Dev", "DolphinPractices", practiceId));
        const data = snap.exists() ? snap.data() : {};
        return data.ReceptionStatusId || data.receptionStatusId || "c5782f1a-0557-4eea-8143-ba5a23feb59e";
    }


    async function confirmAppointmentGPS(appointmentId) {
        // 1) load creds + status id
        let { practiceId, dolphinApiKey } = await getRobotCreds(savedRobot);
        const gpsStatusId = await getGpsStatusId(practiceId);

        // 2) first attempt
        let res = await putGpsStatus({ practiceId, token: dolphinApiKey, appointmentId, gpsStatusId });

        // 3) on 401 → refesh dolphin api key, then re-read key, then retry once
        if (res.status === 401) {
            try {
                await refreshDolphinAPIKey(practiceId, savedRobot);
                const { dolphinApiKey } = await getRobotCreds(savedRobot);
                res = await putGpsStatus({ practiceId, token: dolphinApiKey, appointmentId, gpsStatusId });
            } catch (e) {
                throw new Error(`Auth refresh failed: ${e.message}`);
            }
        }

        // 4) handle result
        if (res.status >= 200 && res.status < 300) {
            const apptCol = collection(db, `Companies/Zeeno_Dev/Dolphin/${savedRobot}/Appointments`);
            const qAppt = query(apptCol, where("Id", "==", appointmentId));
            const snap = await getDocs(qAppt);
            if (!snap.empty) await updateDoc(snap.docs[0].ref, { Confirmed: true });
            return true;
        }

        // Bubble a readable error for the confirm modal
        const body = typeof res.data === 'string' ? res.data : JSON.stringify(res.data || {});
        throw new Error(`Dolphin ${res.status}: ${body}`);
    }   
  

  // ---------- data load ----------
  const fetchRobot = async (sn) => {
    const ref = doc(db, "Companies", "Zeeno_Dev", "Dolphin", sn);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : {};
  };

  const fetchPatients = async (sn) => {
    const robot = await fetchRobot(sn);
    const qArgs = [
      collection(db, "Companies", "Zeeno_Dev", "Dolphin", sn, "Appointments"),
      where("Confirmed", "==", false),
    ];
    const locations = robot.practiceLocation || [];
    if (locations.length > 0) qArgs.push(where("LocationName", "in", locations));
    qArgs.push(orderBy("OriginalTime"));

    const querySnapshot = await getDocs(query(...qArgs));
    const patients = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    setPatientsList(patients);
  };

  useEffect(() => {
    if (savedRobot) fetchPatients(savedRobot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedRobot]);

  useEffect(() => {
    setShow(isOpen);
    if (!isOpen) setConfirming(null);
  }, [isOpen]);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 200);
  };

  // ---------- UI handlers ----------
  const openConfirm = (appt) => {
    setConfirming({ id: appt.Id, firstName: appt.FirstName || appt.firstName || 'this patient' });
  };
  const cancelConfirm = () => setConfirming(null);

  const doCheckIn = async () => {
    if (!confirming) return;
    setIsCheckingIn(true);
    try {
      const ok = await confirmAppointmentGPS(confirming.id);
      if (ok) {
        // refresh list and close the confirm modal
        await fetchPatients(savedRobot);
        setConfirming(null);
      }
    } catch (e) {
      console.error("Check-in failed:", e);
    } finally {
      setIsCheckingIn(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full transition-opacity duration-200 ${show ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
      style={{ display: show || isOpen ? 'block' : 'none' }}
    >
      <div
        className="relative mx-auto p-6 border w-[75vw] h-[90vh] shadow-lg rounded-md bg-white mt-[5vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-heebo font-medium">Patient Check-in</h2>
          <button
            onClick={handleClose}
            className="text-black bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg p-2 text-sm inline-flex items-center"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Patients list */}
        <div className="flex flex-col h-[calc(90vh-8rem)]">
            <div className="flex-grow overflow-y-auto px-20">
                {patientsList.map((item) => (
                    <div key={item.Id} className="flex justify-center">
                        <PatientRow item={item} onCheck={() => openConfirm(item)} />
                    </div>
                ))}
                {patientsList.length === 0 && (
                    <div className="text-center text-gray-500 mt-10">No scheduled patients.</div>
                )}
            </div>
            {/* Done footer */}
            <div className="mt-auto pt-4 border-t flex justify-center">
                <button
                    type="button"
                    className="flex w-fit bg-[#152FF5] py-2.5 px-8 rounded-3xl items-center cursor-pointer hover:bg-opacity-90 text-white"
                    onClick={handleClose}
                >
                    <span className="font-heebo text-lg">Done</span>
                    <IconTint src={checkIcon} className="h-6 ml-2" color="#FFFFFF" />
                </button>
            </div>
        </div>

        {/* Confirm modal */}
        {confirming && (
          <ConfirmModal
            name={confirming.firstName}
            isBusy={isCheckingIn}
            onCancel={cancelConfirm}
            onConfirm={doCheckIn}
          />
        )}
      </div>
    </div>
  );
};

const PatientRow = ({ item, onCheck }) => {
  return (
    <div className="flex flex-row h-16 bg-[#8290F8] px-4 bg-opacity-10 rounded-l-[100px] rounded-r-3xl items-center mb-3 border-l-[#8290F8] border-l-[5px] w-[80%]">
      <img alt="patient_status" src={statusIcon} className="h-12" />
      <div className="flex flex-col w-40 ml-3">
        <span className="font-heebo text-lg">{item.FirstName}</span>
        <span className="font-heebo font-light text-sm">Patient</span>
      </div>
      <div className="flex flex-col w-44">
        <span className="font-heebo text-lg">{item.ScheduledTimeBack}</span>
        <span className="font-heebo font-light text-sm">Scheduled Time</span>
      </div>
      <div className="flex flex-col w-24">
        <span className="font-heebo text-lg">{item.Chair}</span>
        <span className="font-heebo font-light text-sm">Chair</span>
      </div>
      <img alt="doctor" src={maskIcon} className="h-8 -mt-4" />
      <div className="flex flex-col w-400 ml-3">
        <span className="font-heebo text-lg">{item.LocationName}</span>
        <span className="font-heebo font-light text-sm">Location</span>
      </div>
      <button
        className="ml-auto flex items-center bg-[#152FF5] hover:bg-opacity-90 bg-opacity-80 py-1.5 px-4 rounded-lg text-white"
        onClick={onCheck}
      >
        <span className="font-heebo font-light text-base">Check In</span>
        <IconTint src={checkIcon} className="h-5 ml-2" color="#FFFFFF" />
      </button>
    </div>
  );
};

const ConfirmModal = ({ name, isBusy, onCancel, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-[#1f2a44] text-white rounded-xl p-6 w-[520px] shadow-xl">
        <div className="text-center text-xl mb-6">Check in {name} ?</div>
        <div className="flex justify-center gap-4">
          <button
            className="px-6 py-2 rounded-lg bg-[#3C5BD2] bg-opacity-20 hover:bg-opacity-30"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </button>
          <button
            className="px-6 py-2 rounded-lg bg-[#3C5BD2] hover:bg-opacity-90 disabled:opacity-50"
            onClick={onConfirm}
            disabled={isBusy}
          >
            {isBusy ? 'Checking In…' : 'Check In'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckinPopup;
